import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text, RadioButton } from 'grommet';
import { COUNTRIES } from '@RedflagAI/node-utility/utils/constants.utils';
import { useAppTheme } from '@Components/Context';
import {
  ProductFormSingleTextInput,
  ProductFormDropdownSelect,
} from '@Components/Partial/Product/NewSearch';

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'individual', label: 'Individual', hint: 'Up to 2 YouTube channels' },
  { value: 'agency', label: 'Agency', hint: 'Up to 100 YouTube channels' },
];

const OPTIONAL_HINT = 'Customer will be asked to fill this in if left blank';

const FieldLabel = ({
  label, required = false, theme,
}) => (
  <Box direction="row" gap="0.25rem" align="center" margin={{ bottom: '0.25rem' }}>
    <Text size="0.85rem" weight={600} color={theme.page.primaryText}>{label}</Text>
    {required && <Text size="0.75rem" color="var(--app-error)">*</Text>}
  </Box>
);

FieldLabel.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  theme: PropTypes.object.isRequired,
};

const Step1AccountDetails = ({
  small,
  contact,
  setContact,
  accountType,
  setAccountType,
}) => {
  const { theme } = useAppTheme();
  const { page } = theme;

  return (
    <Box gap="1.25rem">
      <Box gap="0.25rem">
        <FieldLabel label="Email" required theme={theme} />
        <ProductFormSingleTextInput
          type="email"
          small={small}
          focusHighlight={page.focusHighlight}
          value={contact.email}
          handleFormValues={(val) => setContact((prev) => ({ ...prev, email: val }))}
          placeholder="customer@example.com"
        />
      </Box>
      <Box gap="0.25rem">
        <FieldLabel label="Full Name" theme={theme} />
        <ProductFormSingleTextInput
          small={small}
          focusHighlight={page.focusHighlight}
          value={contact.fullName}
          handleFormValues={(val) => setContact((prev) => ({ ...prev, fullName: val }))}
          placeholder="Jane Smith"
        />
        <Text size="0.75rem" color={page.secondaryText} margin={{ top: '0.2rem' }}>
          {OPTIONAL_HINT}
        </Text>
      </Box>
      <Box gap="0.25rem">
        <FieldLabel label="Company / Channel Name" theme={theme} />
        <ProductFormSingleTextInput
          small={small}
          focusHighlight={page.focusHighlight}
          value={contact.companyName}
          handleFormValues={(val) => setContact((prev) => ({ ...prev, companyName: val }))}
          placeholder="Acme Corp"
        />
        <Text size="0.75rem" color={page.secondaryText} margin={{ top: '0.2rem' }}>
          {OPTIONAL_HINT}
        </Text>
      </Box>
      <Box gap="0.25rem">
        <FieldLabel label="Company Phone" theme={theme} />
        <ProductFormSingleTextInput
          type="tel"
          small={small}
          focusHighlight={page.focusHighlight}
          value={contact.phone}
          handleFormValues={(val) => setContact((prev) => ({ ...prev, phone: val }))}
          placeholder="Include country code (e.g. +1 555 000 0000)"
        />
      </Box>
      <Box gap="0.25rem">
        <FieldLabel label="Country" theme={theme} />
        <ProductFormDropdownSelect
          searchable
          keepLabel
          selectable
          simpleOptions={COUNTRIES}
          handleFormValues={(val) => setContact((prev) => ({ ...prev, country: val }))}
          value={contact.country || ''}
          label={contact.country || 'Select country...'}
          placeholder="Select country..."
          focusHighlight={page.focusHighlight}
        />
      </Box>
      <Box gap="0.25rem">
        <FieldLabel label="Account Type" required theme={theme} />
        <Box direction="row" gap="1.5rem" margin={{ top: '0.25rem' }}>
          {ACCOUNT_TYPE_OPTIONS.map((opt) => (
            <Box key={opt.value} direction="row" align="center" gap="0.5rem">
              <RadioButton
                name="accountType"
                value={opt.value}
                checked={accountType === opt.value}
                onChange={() => setAccountType(opt.value)}
                label={(
                  <Box>
                    <Text size="0.875rem" color={page.primaryText}>{opt.label}</Text>
                    <Text size="0.75rem" color={page.secondaryText}>{opt.hint}</Text>
                  </Box>
                )}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

Step1AccountDetails.propTypes = {
  small: PropTypes.bool.isRequired,
  contact: PropTypes.shape({
    email: PropTypes.string.isRequired,
    fullName: PropTypes.string.isRequired,
    companyName: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
  }).isRequired,
  setContact: PropTypes.func.isRequired,
  accountType: PropTypes.string.isRequired,
  setAccountType: PropTypes.func.isRequired,
};

export default Step1AccountDetails;
